import {
  getEventModuleLabel,
  resolveCustomHomeSections,
  resolveEventHomeLayout,
  type ConferenceEvent,
  type EventAppModules,
  type EventHomeLayout,
  type EventPhase,
} from '@conference/shared';

interface HomeLayoutPreviewProps {
  event: ConferenceEvent;
  layout: EventHomeLayout;
  modules: EventAppModules;
  phase: EventPhase;
}

export function HomeLayoutPreview({ event, layout, modules, phase }: HomeLayoutPreviewProps) {
  const blocks = resolveEventHomeLayout(layout, modules, phase);
  const customSections = resolveCustomHomeSections(layout, modules, phase);
  const items = [
    ...blocks.map((block) => ({ kind: 'block' as const, order: block.order, block })),
    ...customSections.map((section) => ({ kind: 'custom' as const, order: section.order, section })),
  ].sort((left, right) => left.order - right.order);

  return (
    <section aria-label="Attendee home preview" className="rounded-[2rem] border-4 border-gray-800 bg-gray-950 p-3 shadow-xl">
      <div className="rounded-[1.4rem] bg-gray-900 p-3">
        <p className="mb-3 truncate text-xs font-semibold text-white">{event.name}</p>
        <div className="space-y-2">
          {items.map((item) => item.kind === 'block' ? (
            <article key={item.block.key} className="rounded-lg border border-gray-800 bg-gray-900 px-3 py-2">
              <p className="text-xs font-semibold text-white">{item.block.title}</p>
              {item.block.key === 'quick_actions' && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {item.block.module_keys.map((key) => (
                    <span key={key} className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-300">
                      {getEventModuleLabel(modules, key)}
                    </span>
                  ))}
                </div>
              )}
              {item.block.key !== 'quick_actions' && item.block.key !== 'event_hero' && (
                <div className="mt-2 h-1.5 w-2/3 rounded bg-gray-800" />
              )}
            </article>
          ) : <article key={item.section.id} className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2">
            <p className="text-xs font-semibold text-white">{item.section.title}</p>
            <p className="mt-1 line-clamp-2 text-[10px] text-gray-400">{item.section.content_style === 'featured_sponsors' ? 'Featured sponsor organizations' : item.section.body_markdown}</p>
            {item.section.image_url && <span className="mt-1 inline-block text-[10px] text-sky-300">Image</span>}
            {item.section.action && <span className="ml-2 mt-1 inline-block text-[10px] text-sky-300">{item.section.action.label}</span>}
          </article>)}
        </div>
      </div>
    </section>
  );
}
